import api from 'api';
import { actions } from 'store';
import { useDispatch, useReduxState } from 'hooks';
import { Alert, ModalButtonWithForm } from 'components/common';
import { FormGroup, Text } from 'components/common/form';

import type { FormState } from 'informed';
import type { UserCustomAttribute } from 'shared-types';

type FormValues = {
  key: string;
  label: string;
  labelTransations: { [key: string]: string };
};

interface Props {
  attribute: UserCustomAttribute;
  optionGroupIndex: number;
  optionIndex: number;
  updateData: () => Promise<void>;
}

const UserAttributesOptionsRenameKey = ({
  attribute,
  optionGroupIndex,
  optionIndex,
  updateData
}: Props) => {
  const { dispatch } = useDispatch();
  const { language, messages } = useReduxState();

  const optionGroup = attribute.optionGroups[optionGroupIndex];

  const option = optionGroup.options[optionIndex];

  const existingKeys = {};
  attribute.optionGroups
    .filter((group) => group.options.some((item) => item.key === option.key))
    .forEach((group) => {
      group.options.forEach((item) => {
        if (item.key !== option.key) existingKeys[item.key] = true;
      });
    });

  const handleSubmit = async (values) => {
    const data = {
      oldKey: option.key,
      newKey: values.key.trim()
    };

    await api.post(
      `/admin/user-custom-attributes/${attribute.id}/rename-key`,
      data
    );

    await updateData();

    dispatch(
      actions.addNotice({
        text: messages.commonChangesSavesSuccessfully,
        type: 'success'
      })
    );
  };

  const validateKey = (value) => {
    if (!value?.trim()) return messages.commonKeyEmpty;

    return existingKeys[value.trim()] ? messages.commonKeyInUse : undefined;
  };

  const initialValues = {
    key: option.key
  };

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="key"
      buttonIsDropdown={true}
      buttonSize="header"
      buttonText={messages.userAttributesRenameKey}
      initialValues={initialValues}
      modalActionText={messages.commonSave}
      modalTitle={messages.userAttributesRenameKey}
      onSubmit={handleSubmit}
      pristineIsValid={false}
    >
      {({ values }: FormState<FormValues>) => (
        <div className="inner">
          <Alert
            text={messages.userAttributesRenameKeyInfo}
            type="warning-light"
          />

          <FormGroup
            label={
              optionGroup.customKeyNameTranslations?.[language] ||
              optionGroup?.customKeyName ||
              messages.attributesKey
            }
          >
            <Text field="key" validate={validateKey} validateOnChange={true} />
          </FormGroup>
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default UserAttributesOptionsRenameKey;
